import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAdminDashboard from '../hook/useAdminDashboard.mjs';
import {
  getStatusColorClass, getStatusLabel, buildStatusFilterOptions,
  getClaimStatus, countByStatus, ADMIN_CLAIM_CARD_SET,
} from '../../utils/statusConfig.mjs';
import ExcelJS from 'exceljs';
import BaseApiService from '../../auditor/service/baseApiService.mjs';
import { useDialog } from '../../components/dialog/DialogProvider.mjs';
import PageShell from '../../components/PageShell.mjs';
import QuestionBadge from '../../clarifications/components/QuestionBadge.mjs';
import { useOpenQuestions } from '../../clarifications/hook/useOpenQuestions.mjs';

const api = new BaseApiService();

const AdminDashboard = () => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { alert } = useDialog();
  const navigate = useNavigate();
  // Reviewers' open questions on the region's claims, badged on their rows.
  const { forClaim: questionsForClaim } = useOpenQuestions();

  // Currency of the State Department rate data, shown under the filters.
  const [ratesStatus, setRatesStatus] = useState(null);
  const [ratesDownloading, setRatesDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.api.get('/perdiem/rates/status')
      .then(res => { if (!cancelled) setRatesStatus(res.data?.data || null); })
      .catch(() => { /* non-critical: the dashboard works without it */ });
    return () => { cancelled = true; };
  }, []);

  // Download the stored rates so they can be diffed against the published file.
  const downloadRatesExcel = useCallback(async () => {
    setRatesDownloading(true);
    try {
      const res = await api.api.get('/perdiem/rates/export');
      const data = res.data?.data;
      const rows = data?.rates || [];
      if (rows.length === 0) {
        alert('No rate data available to download.');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Stanford Seed Expense Claim System';
      const ws = workbook.addWorksheet(`Rates ${data.label || ''}`.trim());

      // The sheet now holds two feeds, so it can no longer claim to be State
      // Department data - each row says which authority set it, because the two
      // must be diffed against different published files.
      ws.addRow([`PER DIEM RATES - ${data.label || ''}`]).font =
        { bold: true, size: 14, color: { argb: 'FF8C1515' } };
      ws.addRow([
        `${rows.length} rates` +
        (data.foreignCount != null ? ` · ${data.foreignCount} foreign (State Dept)` : '') +
        (data.domesticCount != null ? ` · ${data.domesticCount} US (GSA)` : '') +
        ` · exported ${new Date().toLocaleString('en-US')}`
      ]);
      ws.addRow([]);

      const header = ws.addRow([
        'Country', 'City', 'Jurisdiction', 'Authority', 'Season', 'Season Start', 'Season End',
        'Lodging', 'M&IE', 'Per Diem',
      ]);
      header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF8C1515' } };

      for (const r of rows) {
        ws.addRow([
          r.country, r.city, r.jurisdiction || '', r.authority || '',
          r.seasonCode, r.seasonStart, r.seasonEnd,
          r.lodging, r.mealsAndIncidentals, r.perDiem,
        ]);
      }
      [26, 30, 12, 22, 10, 14, 14, 12, 12, 12].forEach((w, i) => { ws.getColumn(i + 1).width = w; });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PerDiem_Rates_${(data.label || 'export').replace(/\s+/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not download rates: ' + (err.response?.data?.message || err.message));
    } finally {
      setRatesDownloading(false);
    }
  }, [alert]);

  const {
    claims,
    // The unfiltered population. Card counts MUST come from this, not from
    // allClaims (= filteredClaims): counting the filtered list makes every
    // other card read zero as soon as a status filter is applied.
    rawClaims,
    loading,
    error,
    filters,
    updateFilter,
    updateItemsPerPage,
    clearFilters,
    currentPage,
    totalPages,
    itemsPerPage,
    goToPage,
    totalClaims,
    hasNextPage,
    hasPreviousPage,
    // Clearing the selection is still needed when a status tile changes the
    // filter. The rest of the selection API was only ever used by the draft
    // view's bulk-delete toolbar, which is gone.
    clearSelection,
  } = useAdminDashboard();

  const handleViewClaim = (claim) => {
    navigate(`/admin/${claim.claimType}/claims/${claim._id}`);
  };

  const handlePageChange = (page) => {
    goToPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    updateItemsPerPage(Number(newItemsPerPage));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  // "Admin Dashboard" is the page of cards whose Claims Approval card opens this.
  const shellProps = { title: 'Claims Approval', subtitle: 'Manage and review expense claims' };

  if (loading) {
    return (
      <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading claims...</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell {...shellProps}>
        {/* Filters Section */}
        <div className="dashboard-filters">
          <div className="dashboard-filters-title">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filter Claims
          </div>
          <div className="dashboard-filters-grid">
            <div className="dashboard-filter-group">
              <label className="dashboard-filter-label">Claim Type</label>
              <select
                value={filters.claimType}
                onChange={(e) => updateFilter('claimType', e.target.value)}
                className="dashboard-filter-select"
              >
                <option value="">All Types</option>
                <option value="perdiem">Per Diem</option>
                <option value="expense">Expense</option>
              </select>
            </div>

            <div className="dashboard-filter-group">
              <label className="dashboard-filter-label">Status</label>
              <select
                value={filters.status}
                onChange={(e) => { updateFilter('status', e.target.value); clearSelection(); }}
                className="dashboard-filter-select"
              >
                <option value="">All Statuses</option>
                {buildStatusFilterOptions(rawClaims).map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="dashboard-filter-group">
              <label className="dashboard-filter-label">Year</label>
              <select
                value={filters.year}
                onChange={(e) => updateFilter('year', e.target.value)}
                className="dashboard-filter-select"
              >
                <option value="">All Years</option>
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>{year}</option>
                  );
                })}
              </select>
            </div>

            <div className="dashboard-filter-group">
              <label className="dashboard-filter-label">Month</label>
              <select
                value={filters.month}
                onChange={(e) => updateFilter('month', e.target.value)}
                className="dashboard-filter-select"
              >
                <option value="">All Months</option>
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('en', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={clearFilters}
              className="dashboard-refresh-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              Clear filters
            </button>
          </div>

          {/* How current each rate feed is. There are two, on unrelated schedules:
              State.gov publishes a month's file partway through that month (so the
              newest foreign month can legitimately lag), while GSA publishes a
              whole fiscal year at once (so the domestic end date runs ahead).
              Showing one figure for both made the foreign data look fresher than
              it was. */}
          {ratesStatus && (
            <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
              <span>
                Foreign rates <span className="text-gray-400">(State Dept)</span>:{' '}
                <span className="font-semibold text-gray-800">
                  {ratesStatus.sources?.foreign?.label || ratesStatus.label || '-'}
                </span>
                {ratesStatus.fetchedAt && (
                  <span className="text-gray-500">
                    {' '}(fetched {new Date(ratesStatus.fetchedAt).toLocaleString('en-US')})
                  </span>
                )}
              </span>
              <span>
                US rates <span className="text-gray-400">(GSA)</span>:{' '}
                <span className={`font-semibold ${ratesStatus.sources?.domestic ? 'text-gray-800' : 'text-amber-700'}`}>
                  {ratesStatus.sources?.domestic
                    ? `through ${ratesStatus.sources.domestic.label}`
                    : 'not loaded'}
                </span>
              </span>
              {ratesStatus.manualEntryRegions && (
                <span
                  className="text-xs text-gray-500 cursor-help"
                  title={`${ratesStatus.manualEntryRegions.regions.join(', ')} - ${ratesStatus.manualEntryRegions.note}`}
                >
                  Alaska, Hawaii &amp; US territories: entered manually ⓘ
                </span>
              )}
              <button
                onClick={downloadRatesExcel}
                disabled={ratesDownloading}
                className="px-3 py-1 text-xs border border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-colors disabled:opacity-50"
                title="Download these rates to compare against the published file"
              >
                {ratesDownloading ? 'Preparing…' : 'Download rates (Excel)'}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl flex items-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="font-medium">{error}</p>
          </div>
        )}

        {/* Status cards.
            One per claim status, counted over the whole population and each one
            a filter control. Previously four cards (submitted / returned /
            approved / rejected) computed over the FILTERED list, so
            'under_review' and 'paid' had no representation at all and applying
            any filter zeroed every card but one. Drafts are deliberately absent:
            the admin API does not return them. */}
        {(() => {
          const counts = countByStatus(rawClaims, ADMIN_CLAIM_CARD_SET);
          return (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-3 mb-6">
              {ADMIN_CLAIM_CARD_SET.map((value) => {
                const meta = getClaimStatus(value);
                const active = filters.status === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      updateFilter('status', active ? '' : value);
                      clearSelection();
                    }}
                    aria-pressed={active}
                    title={meta.description}
                    className={`text-left bg-white rounded-xl border p-3 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-stanford-red ${
                      active ? 'border-stanford-red ring-1 ring-stanford-red' : meta.border
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${meta.solid}`} />
                      <span className="text-2xl font-bold text-gray-900 leading-none">
                        {counts[value]}
                      </span>
                    </div>
                    <div className="mt-1.5 text-xs font-semibold text-gray-700 leading-snug">
                      {meta.label}
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })()}

        {/* Total Overview Banner */}
        <div className="bg-stanford-red rounded-2xl p-5 sm:p-6 mb-6 sm:mb-8 text-white shadow-xl">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-lg font-medium text-gray-300">Total Claims Overview</h2>
              <p className="text-2xl sm:text-3xl font-bold">{totalClaims} Claims</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-400">Filtered Results</p>
              <p className="text-xl font-semibold">{claims.length} showing</p>
            </div>
          </div>
        </div>

        {/* Pagination Controls - Top */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 mb-4 sm:mb-6 bg-white rounded-xl px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-600 whitespace-nowrap">Items per page:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(e.target.value)}
              className="dashboard-filter-select !min-w-[80px]"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="text-sm text-gray-600 font-medium">
            Showing <span className="text-stanford-red">{totalClaims > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0}</span> to <span className="text-stanford-red">{Math.min(currentPage * itemsPerPage, totalClaims)}</span> of <span className="font-bold">{totalClaims}</span> entries
          </div>
        </div>

        {/* Claims Table */}
        <div className="dashboard-table-container">
          <div className="overflow-x-auto">
            <table className="dashboard-table responsive-table">
              <thead className="dashboard-table-header">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Date Created</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {claims.map(claim => (
                  <tr key={claim._id} className="dashboard-table-row">
                    <td className="dashboard-table-cell" data-label="Type">
                      <span className="inline-flex items-center px-3 py-1 rounded-lg bg-gray-100 text-gray-700 font-medium text-xs uppercase">
                        {claim.claimType}
                      </span>
                    </td>
                    <td className="dashboard-table-cell font-medium text-gray-900" data-label="Employee">
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        {claim.user ?
                          `${claim.user.firstName} ${claim.user.lastName}` :
                          'Unknown User'
                        }
                        {/* A reviewer's question waiting on the region - the claim
                            page lists them with a link to answer. */}
                        <QuestionBadge
                          threads={questionsForClaim(claim.claimType, claim._id)}
                          onClick={() => handleViewClaim(claim)}
                        />
                      </span>
                    </td>
                    <td className="dashboard-table-cell text-gray-600" data-label="Created">{formatDate(claim.createdAt)}</td>
                    <td className="dashboard-table-cell font-bold text-gray-900 tabular-nums" data-label="Amount">{formatCurrency(claim.totalUsdAmount ?? claim.totalClaim ?? claim.totalAmount ?? 0)}</td>
                    <td className="dashboard-table-cell" data-label="Status">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColorClass(claim.status)}`}>
                        {getStatusLabel(claim.status)}
                      </span>
                    </td>
                    <td className="dashboard-table-cell" data-cell="action">
                      <button
                        className="view-details-btn"
                        onClick={() => handleViewClaim(claim)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {claims.length === 0 && !loading && (
            <div className="dashboard-empty-state">
              <div className="dashboard-empty-icon">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="dashboard-empty-title">No Claims Found</h3>
              <p className="dashboard-empty-text">No claims match your current filter criteria.</p>
            </div>
          )}
        </div>

        {/* Pagination Controls - Bottom */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6 sm:mt-8 bg-white rounded-xl px-3 sm:px-6 py-3 sm:py-4">
            <button
              className="hidden sm:block px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </button>

            <button
              className="min-h-[44px] px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!hasPreviousPage}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>

            {/* Phones: Previous / "Page x of y" / Next - the numbered row did not fit. */}
            <span className="sm:hidden px-2 text-sm text-gray-600 tabular-nums">
              Page {currentPage} of {totalPages}
            </span>
            <div className="hidden sm:flex gap-1">
              {getPageNumbers().map(page => (
                <button
                  key={page}
                  className={`px-4 py-2 text-sm font-medium rounded-xl transition-all ${page === currentPage ? 'bg-stanford-red text-white shadow-lg' : 'border border-gray-200 hover:bg-gray-50'}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              className="min-h-[44px] px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!hasNextPage}
            >
              Next
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              className="hidden sm:block px-4 py-2 text-sm font-medium border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </div>
        )}
    </PageShell>
  );
};

export default AdminDashboard;
